import { AppLayout } from "@/components/AppLayout";
import { User, Lock, Bell, Palette, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const Settings = () => {
  const { toast } = useToast();
  const [profile, setProfile] = useState({
    username: "Utilisateur",
    email: "user@example.com",
    bio: "",
  });
  const [notifications, setNotifications] = useState({
    email: true,
    marketing: false,
    updates: true,
  });

  const handleSaveProfile = () => {
    toast({
      title: "Profil mis à jour",
      description: "Vos informations ont été enregistrées.",
    });
  };

  const handleChangePassword = () => {
    toast({
      title: "Email envoyé",
      description: "Un lien de réinitialisation vous a été envoyé.",
    });
  };

  return (
    <AppLayout title="Paramètres">
      <div className="p-6 max-w-4xl">
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-secondary rounded-xl p-1">
            <TabsTrigger value="profile" className="data-[state=active]:bg-card rounded-lg">
              <User className="w-4 h-4 mr-2" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="password" className="data-[state=active]:bg-card rounded-lg">
              <Lock className="w-4 h-4 mr-2" />
              Mot de passe
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-card rounded-lg">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="preferences" className="data-[state=active]:bg-card rounded-lg">
              <Palette className="w-4 h-4 mr-2" />
              Préférences
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-foreground">Informations du profil</h2>
              
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-full bg-gradient-floop flex items-center justify-center">
                  <User className="w-10 h-10 text-white" />
                </div>
                <Button variant="outline" className="rounded-xl">
                  Changer la photo
                </Button>
              </div>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Nom d'utilisateur</label>
                  <Input
                    value={profile.username}
                    onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                    className="bg-input border-border rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    className="bg-input border-border rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Bio</label>
                  <Input
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    placeholder="Parlez-nous de vous..."
                    className="bg-input border-border rounded-xl"
                  />
                </div>
              </div>

              <Button onClick={handleSaveProfile} className="bg-gradient-floop hover:opacity-90 text-white rounded-xl">
                Enregistrer les modifications
              </Button>
            </div>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-foreground">Changer le mot de passe</h2>
              
              <div className="grid gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Mot de passe actuel</label>
                  <Input type="password" className="bg-input border-border rounded-xl" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Nouveau mot de passe</label>
                  <Input type="password" className="bg-input border-border rounded-xl" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Confirmer le nouveau mot de passe</label>
                  <Input type="password" className="bg-input border-border rounded-xl" />
                </div>
              </div>

              <div className="flex gap-4">
                <Button onClick={handleChangePassword} className="bg-gradient-floop hover:opacity-90 text-white rounded-xl">
                  Mettre à jour le mot de passe
                </Button>
                <Button variant="outline" className="rounded-xl">
                  Mot de passe oublié ?
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-foreground">Préférences de notification</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                  <div>
                    <div className="font-medium text-foreground">Notifications par email</div>
                    <div className="text-sm text-muted-foreground">Recevez des notifications sur vos générations</div>
                  </div>
                  <Switch
                    checked={notifications.email}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                  <div>
                    <div className="font-medium text-foreground">Emails marketing</div>
                    <div className="text-sm text-muted-foreground">Recevez nos offres et nouveautés</div>
                  </div>
                  <Switch
                    checked={notifications.marketing}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, marketing: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                  <div>
                    <div className="font-medium text-foreground">Mises à jour produit</div>
                    <div className="text-sm text-muted-foreground">Soyez informé des nouvelles fonctionnalités</div>
                  </div>
                  <Switch
                    checked={notifications.updates}
                    onCheckedChange={(checked) => setNotifications({ ...notifications, updates: checked })}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6">
            <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
              <h2 className="text-lg font-semibold text-foreground">Préférences d'affichage</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium text-foreground">Langue</div>
                      <div className="text-sm text-muted-foreground">Français</div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-xl">
                    Modifier
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                  <div className="flex items-center gap-3">
                    <Palette className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium text-foreground">Thème</div>
                      <div className="text-sm text-muted-foreground">Sombre</div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-xl">
                    Modifier
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Settings;
